/**
 * IdAustriaButton
 *
 * Dedicated login button for the Austrian national E-ID "ID Austria".
 *
 * ID Austria is an OIDC flow with PKCE/nonce and has its OWN start route
 * (/api/auth/oauth/id_austria/login/start) — it must NOT go through the generic
 * /{provider}/authorize path. The button performs a full-page navigation (the
 * backend answers with a 302 redirect to the ID Austria authorization server).
 *
 * Branding (login mode): styled like the Google/Microsoft rows (full-width
 * outline button, "Mit ID Austria anmelden") with the OFFICIAL ID Austria
 * signet icon from the Logopaket (Logo Guide 2024) — colour signet on light
 * background, white signet on dark. Asset: /media/id-austria/icon-color.svg
 * and icon-white.svg.
 *
 * Link mode (settings): there is no official "verknüpfen" button, so we keep a
 * neutral text button (ShieldCheck + "ID Austria verknüpfen").
 */

'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * @param {object} props
 * @param {('login'|'link')} [props.mode='login']  login = start route, link = settings link route
 * @param {string} [props.returnTo]  optional internal path to return to after the flow
 * @param {string} [props.className]
 */
export default function IdAustriaButton({ mode = 'login', returnTo, className = '' }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    setLoading(true);
    // login → public start route (no session). link → authenticated link route.
    const base =
      mode === 'link'
        ? '/api/auth/oauth/id_austria/link/start'
        : '/api/auth/oauth/id_austria/login/start';
    const url = returnTo
      ? `${base}?return_to=${encodeURIComponent(returnTo)}`
      : base;
    // Full-page navigation: this is an OIDC 302 redirect flow, NOT a fetch.
    window.location.href = url;
  };

  // editable:false → plain string for aria-label attribute (t() returns a React
  // element otherwise → "[object Object]").
  const ariaLabel =
    mode === 'link'
      ? t('auth.id_austria.link_button', {
          defaultValue: 'ID Austria verknüpfen',
          editable: false,
        })
      : t('auth.id_austria.login_button', {
          defaultValue: 'Mit ID Austria anmelden',
          editable: false,
        });

  // LINK mode (settings): keep the neutral text button — no official
  // "verknüpfen" asset exists.
  if (mode === 'link') {
    return (
      <Button
        type="button"
        variant="outline"
        size="lg"
        onClick={handleClick}
        disabled={loading}
        aria-label={ariaLabel}
        className={`w-full justify-start min-h-[44px] ${className}`}
      >
        {loading ? (
          <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          <ShieldCheck className="mr-2 h-5 w-5 text-primary" aria-hidden="true" />
        )}
        {t('auth.id_austria.link_button', 'ID Austria verknüpfen')}
      </Button>
    );
  }

  // LOGIN mode (signin): full-width button matching the Google/Microsoft rows
  // (variant=outline, w-full justify-start, icon + "Mit ID Austria anmelden"),
  // using the official ID Austria signet icon (colour on light, white on dark).
  return (
    <Button
      type="button"
      variant="outline"
      size="lg"
      onClick={handleClick}
      disabled={loading}
      aria-label={ariaLabel}
      className={`w-full justify-start min-h-[44px] ${className}`}
    >
      {loading ? (
        <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
      ) : (
        <span className="mr-2 flex items-center" aria-hidden="true">
          <img
            src="/media/id-austria/icon-color.svg"
            alt=""
            draggable={false}
            className="block dark:hidden h-6 w-auto select-none"
          />
          <img
            src="/media/id-austria/icon-white.svg"
            alt=""
            draggable={false}
            className="hidden dark:block h-6 w-auto select-none"
          />
        </span>
      )}
      {t('auth.id_austria.login_button', 'Mit ID Austria anmelden')}
    </Button>
  );
}
